/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Content type classification for atmospheric image URL composition.
 * Determines which `imageType` and optional `fallbackPolicy` to use.
 */
export type TAtmosphericContentType = "episode" | "movie" | "series";

/**
 * Parameters required to compose an atmospheric image URL.
 */
export type TAtmosphericImageParams = {
  /** Base image host URL from TSRI (e.g. `https://imagegenerator.…/`) */
  baseImageUrl: string;
  /** The object identifier: `contentId` for episodes, `collectionId` for movies/series */
  objectId: string;
  /** Content type determines imageType + fallbackPolicy */
  contentType: TAtmosphericContentType;
  /** Requested image width in CSS pixels */
  width: number;
  /** Requested image height in CSS pixels */
  height: number;
};

const IMAGE_TYPE_ATMOSPHERIC: string = "atmospheric";
const IMAGE_TYPE_EPISODE_BANNER: string = "episodeBanner";
const FALLBACK_POLICY_EPISODE: string = "episodeToSeries";
const EXTENSION_JPG: string = ".jpg";

/**
 * Builds an atmospheric (backdrop) image URL for the Content Details screen.
 *
 * Rules:
 * - Episode → `imageType=episodeBanner` + `fallbackPolicy=episodeToSeries`
 * - Movie  → `imageType=atmospheric` (objectId must be `collectionId`)
 * - Series → `imageType=atmospheric`
 * - Always appends `&extension=.jpg`
 *
 * @returns Composed URL string, or `""` if `baseImageUrl` or `objectId` is empty.
 */
export function buildAtmosphericImageUrl(
  params: TAtmosphericImageParams
): string {
  const { baseImageUrl, objectId, contentType, width, height } = params;

  if (baseImageUrl === "" || objectId === "") {
    return "";
  }

  const imageType: string =
    contentType === "episode"
      ? IMAGE_TYPE_EPISODE_BANNER
      : IMAGE_TYPE_ATMOSPHERIC;

  let url: string =
    baseImageUrl +
    "/images?objectId=" +
    encodeURIComponent(objectId) +
    "&imageType=" +
    imageType +
    "&width=" +
    String(width) +
    "&height=" +
    String(height);

  if (contentType === "episode") {
    url += "&fallbackPolicy=" + FALLBACK_POLICY_EPISODE;
  }

  url += "&extension=" + EXTENSION_JPG;

  return url;
}
